import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plane, Smile } from "lucide-react";
import { toast } from "sonner";
import { toInputDate, fromInputDate } from "../../lib/trips";

const TYPES = [
  { value: "vuelo", label: "Vuelo ✈️" },
  { value: "paquete", label: "Paquete 🌴" },
  { value: "circuito", label: "Circuito 🗺️" },
  { value: "crucero", label: "Crucero 🚢" },
  { value: "otros", label: "Otros 🧳" },
];

const emptyForm = (t) => ({
  trip_destination: t?.trip_destination || "",
  trip_type: t?.trip_type || "",
  trip_date: toInputDate(t?.trip_date),
  return_date: toInputDate(t?.return_date),
  trip_details: t?.trip_details || "",
  post_reminder_enabled: t?.post_reminder_enabled ?? true,
  post_reminder_hours_after: t?.post_reminder_hours_after || 48,
});

export const TripDialog = ({ open, onOpenChange, trip, clientName, onSave }) => {
  const [form, setForm] = useState(emptyForm(trip));
  const [saving, setSaving] = useState(false);
  const isEdit = !!trip?.id;

  useEffect(() => {
    if (open) setForm(emptyForm(trip));
  }, [open, trip]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.trip_date && form.return_date && form.return_date < form.trip_date) {
      toast.error("La fecha de regreso no puede ser anterior a la salida");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        trip_destination: form.trip_destination.trim() || null,
        trip_type: form.trip_type || null,
        trip_date: fromInputDate(form.trip_date),
        return_date: fromInputDate(form.return_date),
        trip_details: form.trip_details.trim() || null,
        ...(form.return_date && {
          post_reminder_enabled: !!form.post_reminder_enabled,
          post_reminder_hours_after: Math.max(1, Number(form.post_reminder_hours_after) || 48),
        }),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trip-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-sky-900 text-xl flex items-center gap-2">
            <Plane className="w-5 h-5" />
            {isEdit ? "Editar viaje" : "Nuevo viaje"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Actualizá los datos del viaje" : "Se usan los datos ya cargados de"}{" "}
            <strong className="text-sky-900">{clientName}</strong>
            {isEdit ? "." : ", no hace falta volver a cargarlos."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-sky-900 font-medium">Destino</Label>
              <Input value={form.trip_destination} onChange={set("trip_destination")} placeholder="Ej: Cancún" className="border-sky-200" data-testid="trip-form-destination" />
            </div>
            <div>
              <Label className="text-sky-900 font-medium">Tipo de viaje</Label>
              <Select value={form.trip_type} onValueChange={set("trip_type")}>
                <SelectTrigger className="border-sky-200" data-testid="trip-form-type">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sky-900 font-medium">Fecha de salida</Label>
              <Input type="date" value={form.trip_date} onChange={set("trip_date")} className="border-sky-200" data-testid="trip-form-departure" />
            </div>
            <div>
              <Label className="text-sky-900 font-medium">Fecha de regreso</Label>
              <Input type="date" value={form.return_date} min={form.trip_date || undefined} onChange={set("return_date")} className="border-sky-200" data-testid="trip-form-return" />
            </div>
          </div>
          <div>
            <Label className="text-sky-900 font-medium">Detalles</Label>
            <Input value={form.trip_details} onChange={set("trip_details")} placeholder="Aeropuerto de salida, hotel, primera parada…" className="border-sky-200" data-testid="trip-form-details" />
          </div>
          {form.return_date ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="trip-form-post-trip">
              <label className="flex items-center gap-2 text-sm text-sky-900 font-medium cursor-pointer">
                <Switch
                  checked={!!form.post_reminder_enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, post_reminder_enabled: v }))}
                  className="data-[state=checked]:bg-amber-500"
                  data-testid="trip-form-post-trip-toggle"
                />
                <Smile className="w-4 h-4 text-amber-500" />
                Encuesta post viaje
              </label>
              {form.post_reminder_enabled && (
                <div className="flex items-center gap-2 text-sm text-sky-800">
                  <Input
                    type="number"
                    min={1}
                    value={form.post_reminder_hours_after}
                    onChange={set("post_reminder_hours_after")}
                    className="w-20 h-8 border-sky-200 bg-white"
                    data-testid="trip-form-post-trip-hours"
                  />
                  horas después del regreso
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-sky-600/70">
              Recomendado: cargá la fecha de regreso para programar la encuesta "Bienvenido de vuelta".
            </p>
          )}
          <Button type="submit" className="w-full btn-sky text-white" disabled={saving} data-testid="trip-form-submit">
            {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear viaje"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
